import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  Fade,
  Zoom,
  CircularProgress,
  Tooltip,
  Button,
  Alert,
} from "@mui/material";

import {
  Send as SendIcon,
  Person as UserIcon,
  ContentCopy as CopyIcon,
  Refresh as RegenerateIcon,
  Close as CloseIcon,
  Lightbulb as SuggestionIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  Menu as MenuIcon,
  ChatBubbleOutline as ChatIcon,
} from "@mui/icons-material";
import OruclicksAIIcon from "./OruclicksAIIcon";
import { AIAgentChat as AIService, getAIChatHistory } from "../../Action/n8nApi";

// ─── Month / history helpers ─────────────────────────────────────────────────
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const formatMonthYear = (date) => `${MONTHS[date.getMonth()]}-${date.getFullYear()}`;

const shiftMonth = (date, delta) => {
  const d = new Date(date.getFullYear(), date.getMonth() + delta, 1);
  return d;
};

const isCurrentMonth = (date) => {
  const now = new Date();
  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
};

// Map API conversation → internal { id, title, messages, openAiConversationId, updatedAt }
const mapApiConversation = (apiConv) => {
  const msgs = [];
  (apiConv.openAiConversationData || []).forEach((item, idx) => {
    msgs.push({ id: `${apiConv.openAiConversationId}_q_${idx}`, type: "user", content: item.question, timestamp: new Date(item.timestamp) });
    msgs.push({ id: `${apiConv.openAiConversationId}_a_${idx}`, type: "ai",  content: item.answer,   timestamp: new Date(item.timestamp) });
  });
  const firstQ = apiConv.openAiConversationData?.[0]?.question || "Conversation";
  return {
    id: apiConv.openAiConversationId,
    title: firstQ.slice(0, 60) + (firstQ.length > 60 ? "\u2026" : ""),
    messages: msgs,
    openAiConversationId: apiConv.openAiConversationId,
    updatedAt: apiConv.updatedAt,
  };
};

const AIChat = ({
  open,
  onClose,
  onContentGenerated,
  initialPrompt = "",
  placeholder = "Ask AI to help you create amazing content...",
  title = "✨ AI Content Assistant",
}) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState(initialPrompt);
  const [isTyping, setIsTyping] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");
  const [copiedIndex, setCopiedIndex] = useState(null);
  // History state
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [hoveredConvId, setHoveredConvId] = useState(null);
  const [monthDate, setMonthDate] = useState(() => new Date());
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const conversationIdRef = useRef(null); // openAiConversationId from API
  const activeConvIdRef = useRef(null);   // mirrors activeConvId for callbacks

  useEffect(() => { activeConvIdRef.current = activeConvId; }, [activeConvId]);

  // Suggested prompts for inspiration
  const suggestedPrompts = [
    "Write an engaging social media post about technology trends",
    "Create a motivational post for entrepreneurs",
    "Generate a product launch announcement",
    "Write a post about work-life balance",
    "Create content for a fitness brand",
    "Generate a travel post with adventure themes",
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    }
  }, [open]);

  // ─── Fetch history from API ───────────────────────────────────────────────
  const fetchHistory = useCallback(async (date) => {
    setIsLoadingHistory(true);
    setHistoryError("");
    try {
      const monthYear = formatMonthYear(date);
      const data = await getAIChatHistory(monthYear);
      const mapped = data
        .map(mapApiConversation)
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      setConversations(mapped);
    } catch (err) {
      console.error("fetchHistory error:", err);
      setHistoryError("Could not load history.");
      setConversations([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  // Load history + reset when dialog opens
  useEffect(() => {
    if (open) {
      const now = new Date();
      setMonthDate(now);
      fetchHistory(now);
      setMessages([]);
      setError("");
      setInputValue(initialPrompt);
      setActiveConvId(null);
      conversationIdRef.current = null;
    }
  }, [open, initialPrompt, fetchHistory]);

  // ─── Month navigation ─────────────────────────────────────────────────────
  const goToPrevMonth = () => {
    setMonthDate((prev) => {
      const next = shiftMonth(prev, -1);
      fetchHistory(next);
      return next;
    });
  };

  const goToNextMonth = () => {
    setMonthDate((prev) => {
      const next = shiftMonth(prev, 1);
      fetchHistory(next);
      return next;
    });
  };

  // ─── Load a past conversation ─────────────────────────────────────────────
  const loadConversation = (conv) => {
    setMessages(conv.messages.map((m) => ({ ...m, timestamp: new Date(m.timestamp) })));
    setActiveConvId(conv.id);
    conversationIdRef.current = conv.openAiConversationId || null;
    setError("");
  };

  // ─── Start a new chat ─────────────────────────────────────────────────────
  const startNewChat = () => {
    // Refetch history so the just-completed conversation appears in the list
    fetchHistory(monthDate);
    setMessages([]);
    setActiveConvId(null);
    conversationIdRef.current = null;
    setError("");
    setInputValue("");
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isGenerating) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    setError("");

    // Add user message
    const newUserMessage = {
      id: Date.now(),
      type: "user",
      content: userMessage,
      timestamp: new Date(),
    };

    const updatedWithUser = [...messages, newUserMessage];
    setMessages(updatedWithUser);
    setIsGenerating(true);
    setIsTyping(true);

    try {
      const { response: aiResponse, openAiConversationId } = await AIService(
        userMessage,
        conversationIdRef.current
      );
      conversationIdRef.current = openAiConversationId;

      const newAIMessage = {
        id: Date.now() + 1,
        type: "ai",
        content: aiResponse,
        timestamp: new Date(),
      };
      const finalMessages = [...updatedWithUser, newAIMessage];
      setMessages(finalMessages);
    } catch (err) {
      const errorMessage = {
        id: Date.now() + 1,
        type: "error",
        content: err.message || "Failed to get AI response. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsGenerating(false);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
      console.error("Failed to copy content:", error);
    }
  };

  const applyContent = (content) => {
    if (onContentGenerated) {
      onContentGenerated(content);
    }
  };

  const applySuggestedPrompt = (prompt) => {
    setInputValue(prompt);
    inputRef.current?.focus();
  };

  const retryLastMessage = () => {
    if (messages.length >= 2) {
      const lastUserMessage = messages[messages.length - 2];
      if (lastUserMessage.type === "user") {
        setInputValue(lastUserMessage.content);
        setMessages((prev) => prev.slice(0, -2));
        setError("");
      }
    }
  };

  // No date grouping needed — conversations are already scoped to a single month

  if (!open) return null;

  return (
    <Fade in={open} timeout={300}>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          bgcolor: "rgba(0, 0, 0, 0.8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          backdropFilter: "blur(8px)",
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <Zoom in={open} timeout={400}>
          <Paper
            sx={{
              width: { xs: "100%", sm: "97%", md: "94%", lg: "90%" },
              height: { xs: "100%", sm: "97%", md: "95%" },
              maxWidth: 1400,
              maxHeight: "97vh",
              borderRadius: { xs: 0, sm: 3 },
              overflow: "hidden",
              display: "flex",
              flexDirection: "row",
              boxShadow: "0 32px 64px -16px rgba(0,0,0,0.6)",
            }}
          >
            {/* ── Sidebar ── */}
            <Box
              sx={{
                width: sidebarOpen ? { xs: "100%", sm: 265 } : 0,
                minWidth: sidebarOpen ? { xs: "100%", sm: 265 } : 0,
                display: { xs: sidebarOpen ? "flex" : "none", sm: "flex" },
                flexDirection: "column",
                bgcolor: "#111827",
                borderRight: "1px solid rgba(255,255,255,0.06)",
                transition: "width 0.25s ease, min-width 0.25s ease",
                overflow: "hidden",
                position: { xs: "absolute", sm: "relative" },
                top: 0, left: 0,
                height: "100%",
                zIndex: 10,
              }}
            >
              {/* Sidebar header */}
              <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}>
                <Typography sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 700, fontSize: "0.82rem", letterSpacing: 0.3 }}>
                  Chat History
                </Typography>
                <Tooltip title="Hide sidebar">
                  <IconButton size="small" onClick={() => setSidebarOpen(false)} sx={{ color: "rgba(255,255,255,0.4)", "&:hover": { color: "white" }, p: 0.5 }}>
                    <MenuIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Month navigation */}
              <Box sx={{ px: 1.5, pt: 1, pb: 0.5, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <Tooltip title="Previous month">
                  <IconButton size="small" onClick={goToPrevMonth} disabled={isLoadingHistory}
                    sx={{ color: "rgba(255,255,255,0.55)", p: 0.5, "&:hover": { color: "white" } }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
                  </IconButton>
                </Tooltip>
                <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.75rem", fontWeight: 600, letterSpacing: 0.4 }}>
                  {formatMonthYear(monthDate)}
                </Typography>
                <Tooltip title={isCurrentMonth(monthDate) ? "Already at current month" : "Next month"}>
                  <span>
                    <IconButton size="small" onClick={goToNextMonth} disabled={isLoadingHistory || isCurrentMonth(monthDate)}
                      sx={{ color: isCurrentMonth(monthDate) ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.55)", p: 0.5, "&:hover": { color: "white" } }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {/* New Chat button */}
              <Box sx={{ px: 1.5, pt: 1.5, pb: 1, flexShrink: 0 }}>
                <Button
                  fullWidth
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={startNewChat}
                  sx={{
                    justifyContent: "flex-start",
                    color: "rgba(255,255,255,0.85)",
                    bgcolor: "rgba(255,255,255,0.07)",
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    py: 0.9,
                    border: "1px solid rgba(255,255,255,0.1)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.13)" },
                  }}
                >
                  New Chat
                </Button>
              </Box>

              {/* Conversation list */}
              <Box
                sx={{
                  flex: 1, overflowY: "auto", px: 1, pb: 2,
                  "&::-webkit-scrollbar": { width: 4 },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                  "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(255,255,255,0.12)", borderRadius: 2 },
                }}
              >
                {isLoadingHistory ? (
                  <Box sx={{ textAlign: "center", pt: 5 }}>
                    <CircularProgress size={22} sx={{ color: "rgba(255,255,255,0.3)" }} />
                    <Typography sx={{ color: "rgba(255,255,255,0.28)", fontSize: "0.72rem", mt: 1.5 }}>Loading…</Typography>
                  </Box>
                ) : historyError ? (
                  <Box sx={{ textAlign: "center", pt: 4, px: 2 }}>
                    <Typography sx={{ color: "#ef4444", fontSize: "0.72rem", mb: 1 }}>{historyError}</Typography>
                    <Button size="small" onClick={() => fetchHistory(monthDate)}
                      sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.7rem", textTransform: "none", bgcolor: "rgba(255,255,255,0.07)", "&:hover": { bgcolor: "rgba(255,255,255,0.13)" } }}
                    >Retry</Button>
                  </Box>
                ) : conversations.length === 0 ? (
                  <Box sx={{ textAlign: "center", pt: 5, px: 2 }}>
                    <ChatIcon sx={{ color: "rgba(255,255,255,0.18)", fontSize: 38, mb: 1 }} />
                    <Typography sx={{ color: "rgba(255,255,255,0.28)", fontSize: "0.75rem" }}>
                      No conversations in {formatMonthYear(monthDate)}
                    </Typography>
                  </Box>
                ) : (
                  conversations.map((conv) => (
                    <Box
                      key={conv.id}
                      onClick={() => loadConversation(conv)}
                      onMouseEnter={() => setHoveredConvId(conv.id)}
                      onMouseLeave={() => setHoveredConvId(null)}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1,
                        px: 1.5, py: 0.8, borderRadius: 1.5, cursor: "pointer",
                        bgcolor: activeConvId === conv.id ? "rgba(255,255,255,0.12)" : "transparent",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.07)" },
                        transition: "background 0.15s",
                      }}
                    >
                      <ChatIcon sx={{ color: "rgba(255,255,255,0.35)", fontSize: 13, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          color: activeConvId === conv.id ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.65)",
                          fontSize: "0.76rem",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {conv.title}
                        </Typography>
                        <Typography sx={{ color: "rgba(255,255,255,0.25)", fontSize: "0.62rem" }}>
                          {new Date(conv.updatedAt).toLocaleDateString([], { month: "short", day: "numeric" })}
                          {" · "}
                          {conv.messages.filter((m) => m.type === "user").length} msg{conv.messages.filter((m) => m.type === "user").length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>

            {/* ── Main Chat Area ── */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>

              {/* Header */}
              <Box sx={{ px: 2.5, py: 1.5, background: "linear-gradient(135deg, #0f172a 0%, #1a1f4e 50%, #0d3d2e 100%)", color: "white", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {!sidebarOpen && (
                    <Tooltip title="Show history">
                      <IconButton size="small" onClick={() => setSidebarOpen(true)} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "white" }, mr: 0.5 }}>
                        <MenuIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Avatar sx={{
                    bgcolor: "transparent", width: 34, height: 34,
                    animation: isGenerating ? "pulse 1.5s infinite" : "none",
                    "@keyframes pulse": { "0%": { transform: "scale(1)" }, "50%": { transform: "scale(1.08)" }, "100%": { transform: "scale(1)" } },
                  }}>
                    <OruclicksAIIcon size={34} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.75, fontSize: "0.68rem" }}>
                      {isGenerating ? "Generating content…" : "Powered by Advanced AI"}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Tooltip title="New Chat">
                    <IconButton onClick={startNewChat} disabled={isGenerating} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "white", bgcolor: "rgba(255,255,255,0.1)" } }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Close">
                    <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "white", bgcolor: "rgba(255,255,255,0.1)" } }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Error Alert */}
              {error && (
                <Box sx={{ px: 2, pt: 1.5, flexShrink: 0 }}>
                  <Alert severity="error" sx={{ borderRadius: 2 }}
                    action={<Button color="inherit" size="small" onClick={retryLastMessage} startIcon={<RegenerateIcon />}>Retry</Button>}
                  >{error}</Alert>
                </Box>
              )}

              {/* Messages */}
              <Box
                sx={{
                  flex: 1, overflow: "auto", p: 2.5, bgcolor: "#f8fafc",
                  display: "flex", flexDirection: "column", gap: 2.5,
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                  "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.13)", borderRadius: 3 },
                }}
              >
                {messages.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <OruclicksAIIcon size={72} style={{ marginBottom: 16, opacity: 0.85 }} />
                    <Typography variant="h6" sx={{ color: "#475569", mb: 1, fontWeight: 700 }}>Welcome to AI Assistant!</Typography>
                    <Typography variant="body2" sx={{ color: "#94a3b8", mb: 4, maxWidth: 480, mx: "auto" }}>
                      Ask me to help you create amazing content for your social media posts
                    </Typography>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: "#64748b", mb: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        <SuggestionIcon sx={{ fontSize: 16 }} /> Try these suggestions:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", maxWidth: 700, mx: "auto" }}>
                        {suggestedPrompts.map((prompt, index) => (
                          <Chip key={index} label={prompt} onClick={() => applySuggestedPrompt(prompt)}
                            sx={{
                              bgcolor: "white", border: "1px solid #e2e8f0", fontSize: "0.78rem",
                              "&:hover": { bgcolor: "#f1f5f9", borderColor: "#667eea" },
                              cursor: "pointer", maxWidth: 240,
                              "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  messages.map((message, index) => (
                    <Fade key={message.id} in={true} timeout={400}>
                      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexDirection: message.type === "user" ? "row-reverse" : "row" }}>
                        <Avatar sx={{
                          bgcolor: message.type === "user" ? "#4B5AB6" : message.type === "error" ? "#ef4444" : "transparent",
                          width: 32, height: 32, flexShrink: 0,
                        }}>
                          {message.type === "user" ? <UserIcon sx={{ fontSize: 18 }} /> : message.type === "error" ? <ErrorIcon sx={{ fontSize: 18 }} /> : <OruclicksAIIcon size={32} />}
                        </Avatar>
                        <Paper sx={{
                          p: 2, maxWidth: { xs: "85%", md: "72%" },
                          bgcolor: message.type === "user" ? "#4B5AB6" : message.type === "error" ? "#fef2f2" : "white",
                          color: message.type === "user" ? "white" : message.type === "error" ? "#dc2626" : "#1e293b",
                          borderRadius: message.type === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                          border: message.type === "error" ? "1px solid #fca5a5" : message.type === "ai" ? "1px solid #e2e8f0" : "none",
                        }}>
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.75, fontSize: "0.9rem" }}>
                            {message.content}
                          </Typography>
                          {message.type === "ai" && (
                            <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap", pt: 1, borderTop: "1px solid #f1f5f9" }}>
                              <Tooltip title={copiedIndex === index ? "Copied!" : "Copy"}>
                                <IconButton size="small" onClick={() => copyToClipboard(message.content, index)}
                                  sx={{ color: copiedIndex === index ? "#10b981" : "#94a3b8", "&:hover": { color: "#10b981" } }}
                                >
                                  <CopyIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                              {onContentGenerated && (
                                <Button size="small" variant="contained" onClick={() => applyContent(message.content)}
                                  sx={{ bgcolor: "#10b981", fontSize: "0.72rem", px: 1.5, py: 0.3, fontWeight: 600, "&:hover": { bgcolor: "#059669" } }}
                                >
                                  Use This Content
                                </Button>
                              )}
                            </Box>
                          )}
                          <Typography variant="caption" sx={{
                            display: "block", mt: 0.5,
                            textAlign: message.type === "user" ? "left" : "right",
                            color: message.type === "user" ? "rgba(255,255,255,0.5)" : "#cbd5e1",
                            fontSize: "0.65rem",
                          }}>
                            {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </Typography>
                        </Paper>
                      </Box>
                    </Fade>
                  ))
                )}

                {/* Typing indicator */}
                {isTyping && (
                  <Fade in={isTyping}>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Avatar sx={{ bgcolor: "transparent", width: 32, height: 32 }}><OruclicksAIIcon size={32} /></Avatar>
                      <Paper sx={{ p: 1.5, bgcolor: "white", borderRadius: "18px 18px 18px 4px", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <CircularProgress size={14} sx={{ color: "#10b981" }} />
                          <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.82rem" }}>AI is thinking…</Typography>
                          <Box sx={{ display: "flex", gap: 0.4 }}>
                            {[0, 1, 2].map((i) => (
                              <Box key={i} sx={{
                                width: 4, height: 4, borderRadius: "50%", bgcolor: "#10b981", opacity: 0.3,
                                animation: `dotPulse 1.5s ease-in-out infinite ${i * 0.3}s`,
                                "@keyframes dotPulse": { "0%, 80%, 100%": { opacity: 0.3 }, "40%": { opacity: 1 } },
                              }} />
                            ))}
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  </Fade>
                )}

                <div ref={messagesEndRef} />
              </Box>

              {/* Input area */}
              <Box sx={{ px: 2.5, py: 2, bgcolor: "white", borderTop: "1px solid #e2e8f0", flexShrink: 0 }}>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-end" }}>
                  <TextField
                    ref={inputRef} fullWidth multiline maxRows={5}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder={placeholder}
                    disabled={isGenerating}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3, bgcolor: "#f8fafc", fontSize: "0.9rem",
                        "&:hover": { bgcolor: "#f1f5f9" },
                        "&.Mui-focused": { bgcolor: "white" },
                        "&.Mui-disabled": { bgcolor: "#f1f5f9", opacity: 0.7 },
                      },
                    }}
                  />
                  <IconButton onClick={handleSendMessage} disabled={!inputValue.trim() || isGenerating}
                    sx={{
                      bgcolor: "#4B5AB6", color: "white", width: 46, height: 46, flexShrink: 0,
                      "&:hover": { bgcolor: "#3a4891" },
                      "&:disabled": { bgcolor: "#cbd5e1", color: "#94a3b8" },
                    }}
                  >
                    {isGenerating ? <CircularProgress size={18} sx={{ color: "white" }} /> : <SendIcon />}
                  </IconButton>
                </Box>
                <Typography variant="caption" sx={{ color: "#94a3b8", mt: 0.8, display: "block", fontSize: "0.7rem" }}>
                  Enter to send · Shift+Enter for new line · {isGenerating ? "Processing…" : "Connected to AI"}
                </Typography>
              </Box>

            </Box>
          </Paper>
        </Zoom>
      </Box>
    </Fade>
  );
};

export default AIChat;
